import { useTranslation } from 'react-i18next'
import { AuditStateBadge } from '../AuditsListView/AuditStateBadge.tsx'
import type { AuditDetail, StageSummary } from '../types.ts'
import { formatDate, formatPeriod } from '../utils/format.ts'
import { AnomaliesPanel } from './Counters/AnomaliesPanel.tsx'
import { CoveragePanel } from './Counters/CoveragePanel.tsx'
import { DraftBackfillPanel } from './Draft/DraftBackfillPanel.tsx'
import { QuotaGauge } from './Quota/QuotaGauge.tsx'
import { StagesPanel } from './Stages/StagesPanel.tsx'

interface AuditViewProps {
  detail: AuditDetail
  stages: StageSummary[]
  anomalies: Map<string, string[]>
  onChanged: () => Promise<void>
}

/**
 * The audit page's composition root (§10.1). Coverage and anomalies are two
 * blocks, apart by design: a call with no known number is coverage, not an
 * error (D-14).
 */
export function AuditView({ detail, stages, anomalies, onChanged }: AuditViewProps) {
  const { t } = useTranslation()
  const { audit } = detail
  const draftUncovered = audit.state === 'draft' && !detail.coverage.covered

  return (
    <div className="flex w-full max-w-6xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{audit.title}</h1>
            <AuditStateBadge state={audit.state} />
          </div>
          <p className="text-xs text-muted">
            {t('pages.audits.page.summary', {
              defaultValue: 'Period {{period}} · cutoff {{cutoff}} · {{count}} pipelines',
              period: formatPeriod(audit.periodFrom, audit.periodTo),
              cutoff: formatDate(audit.cutoffAt),
              count: audit.pipelineIds.length,
            })}
          </p>
          <p className="text-xs text-muted">{audit.pipelineIds.join(' · ')}</p>
        </div>
        <QuotaGauge
          auditId={audit.id}
          state={audit.state}
          spend={detail.spend}
          onChanged={onChanged}
        />
      </header>

      {draftUncovered ? (
        <DraftBackfillPanel missing={detail.coverage.missing} />
      ) : (
        <StagesPanel
          auditId={audit.id}
          state={audit.state}
          stages={stages}
          enabled={detail.stagesEnabled}
          onChanged={onChanged}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CoveragePanel counters={detail.counters} blockedBy={detail.blockedBy} />
        <AnomaliesPanel auditId={audit.id} byKind={anomalies} />
      </div>
    </div>
  )
}
